// src/pages/Docs.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Code, Database, Globe, Shield, ChevronRight, ExternalLink, Search, Menu, X, Terminal, Layout } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

// Shared PageStyles component for consistent styling
const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Azonix&display=swap');
    
    :root {
      --az: #a78bfa;
      --jp: #fbbf24;
      --wt: #06b6d4;
      --neu: #8b5cf6;
      --bg: #08080c;
      --glass: rgba(255, 255, 255, 0.03);
      --gb: rgba(167, 139, 250, 0.15);
      --card-bg: linear-gradient(135deg, rgba(20, 20, 30, 0.9), rgba(10, 10, 15, 0.95));
      --accent-gradient: linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%);
    }
    
    * {
      scrollbar-width: thin;
      scrollbar-color: var(--az) var(--bg);
    }
    
    *::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    
    *::-webkit-scrollbar-track {
      background: var(--bg);
    }
    
    *::-webkit-scrollbar-thumb {
      background: var(--az);
      border-radius: 4px;
    }
    
    .font-azonix {
      font-family: 'Azonix', sans-serif;
    }
    
    .glass-card {
      background: var(--glass);
      backdrop-filter: blur(20px);
      border: 1px solid var(--gb);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05);
    }
    
    .glow-border {
      position: relative;
    }
    
    .glow-border::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 1px;
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.5), rgba(99, 102, 241, 0.3), rgba(139, 92, 246, 0.5));
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }
    
    .gradient-text {
      background: linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .gradient-glow {
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%);
    }
  `}</style>
);

export default function Docs() {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: BookOpen,
      articles: [
        { title: 'Introduction', slug: 'introduction' },
        { title: 'Quick Start Guide', slug: 'quick-start' },
        { title: 'Installation', slug: 'installation' },
        { title: 'Project Structure', slug: 'project-structure' }
      ]
    },
    {
      id: 'api',
      title: 'API Reference',
      icon: Code,
      articles: [
        { title: 'REST API Overview', slug: 'rest-api' },
        { title: 'Authentication', slug: 'authentication' },
        { title: 'Endpoints', slug: 'endpoints' },
        { title: 'Rate Limiting', slug: 'rate-limiting' },
        { title: 'Error Handling', slug: 'error-handling' }
      ]
    },
    {
      id: 'database',
      title: 'Database',
      icon: Database,
      articles: [
        { title: 'MongoDB Setup', slug: 'mongodb-setup' },
        { title: 'Data Models', slug: 'data-models' },
        { title: 'Collections', slug: 'collections' },
        { title: 'Queries', slug: 'queries' }
      ]
    },
    {
      id: 'frontend',
      title: 'Frontend',
      icon: Layout,
      articles: [
        { title: 'React Components', slug: 'react-components' },
        { title: 'State Management', slug: 'state-management' },
        { title: 'Routing', slug: 'routing' },
        { title: 'Styling Guide', slug: 'styling' }
      ]
    },
    {
      id: 'deployment',
      title: 'Deployment',
      icon: Globe,
      articles: [
        { title: 'Vercel Setup', slug: 'vercel-setup' },
        { title: 'Render Deployment', slug: 'render-deployment' },
        { title: 'Environment Variables', slug: 'env-variables' },
        { title: 'CI/CD Pipeline', slug: 'cicd' }
      ]
    },
    {
      id: 'security',
      title: 'Security',
      icon: Shield,
      articles: [
        { title: 'Best Practices', slug: 'security-best-practices' },
        { title: 'API Security', slug: 'api-security' },
        { title: 'Authentication Flow', slug: 'auth-flow' }
      ]
    }
  ];

  const quickLinks = [
    { title: 'API Documentation', description: 'Complete API reference with examples', icon: Code, link: '#api' },
    { title: 'Database Schema', description: 'MongoDB models and relationships', icon: Database, link: '#database' },
    { title: 'Frontend Components', description: 'React component library', icon: Layout, link: '#frontend' },
    { title: 'Deployment Guide', description: 'Deploy to production', icon: Globe, link: '#deployment' }
  ];

  const codeExamples = {
    'rest-api': `
// GET Request Example
const response = await fetch('https://animac-api.com/api/articles', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});
const data = await response.json();
    `,
    'authentication': `
// Authentication Flow
const login = async (email, password) => {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  return response.json();
};
    `,
    'mongodb-setup': `
// MongoDB Connection
const mongoose = require('mongoose');
await mongoose.connect(process.env.MONGODB_URI, {
  useNewUrlParser: true,
  useUnifiedTopology: true
});
    `
  };

  return (
    <>
      <PageStyles />
      <SEO
        title="Documentation - ANIMAC Developer Hub"
        description="Technical documentation, API references, and guides for ANIMAC platform development."
        url="/docs"
        image="/assets/animac-preview-logo.svg"
        type="website"
      />

      <motion.div 
        className="min-h-screen relative overflow-hidden"
        style={{ background: '#08080c' }}
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        transition={{ duration: 0.6 }}
      >
        {/* Background effects */}
        <div className="absolute inset-0 gradient-glow"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `
            radial-gradient(circle at 20% 80%, rgba(167, 139, 250, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.05) 0%, transparent 70%)
          `
        }}></div>

        {/* Header */}
        <header className="fixed top-0 w-full z-50 glass-card backdrop-blur-md border-b border-[rgba(167,139,250,0.15)]">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BookOpen className="text-[#a78bfa]" size={24} />
                <span className="font-azonix font-bold text-lg">ANIMAC Docs</span>
              </div>
              
              {/* Search Bar */}
              <div className="hidden md:flex items-center flex-1 max-w-md mx-8">
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    type="text"
                    placeholder="Search documentation..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 glass-card border border-[rgba(167,139,250,0.3)] rounded-lg text-white text-sm focus:border-[#a78bfa] outline-none"
                  />
                </div>
              </div>

              {/* External Links */}
              <div className="flex items-center gap-3">
                <a
                  href="https://github.com/animac"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  GitHub
                </a>
                <a
                  href="https://discord.gg/animac"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Discord
                </a>
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden p-2 text-gray-400 hover:text-white"
                >
                  {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="flex pt-16">
          {/* Sidebar Navigation */}
          <aside className={`
            fixed md:sticky top-16 left-0 w-64 h-[calc(100vh-4rem)] glass-card border-r border-[rgba(167,139,250,0.15)] overflow-y-auto z-40
            transform transition-transform duration-300
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          `}>
            <nav className="p-4">
              {sections.map((section) => (
                <div key={section.id} className="mb-4">
                  <button
                    onClick={() => setActiveSection(activeSection === section.id ? '' : section.id)}
                    className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-left transition-colors ${
                      activeSection === section.id ? 'glass-card text-[#a78bfa]' : 'text-gray-400 hover:text-white hover:glass-card'
                    }`}
                  >
                    <section.icon size={18} />
                    <span className="font-medium text-sm">{section.title}</span>
                    <ChevronRight size={16} className={`ml-auto transition-transform ${activeSection === section.id ? 'rotate-90' : ''}`} />
                  </button>
                  
                  {activeSection === section.id && (
                    <div className="ml-4 mt-2 space-y-1">
                      {section.articles.map((article) => (
                        <a
                          key={article.slug}
                          href={`#${article.slug}`}
                          className="block px-3 py-1.5 text-sm text-gray-500 hover:text-[#a78bfa] transition-colors"
                        >
                          {article.title}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </aside>

          {/* Mobile Overlay */}
          {mobileMenuOpen && (
            <div 
              className="fixed inset-0 bg-black/50 z-30 md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}

          {/* Main Content */}
          <main className="flex-1 min-h-[calc(100vh-4rem)]">
            {/* Hero Section */}
            <section className="relative py-16">
              <div className="absolute inset-0 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1461749280684-d3baad2994b4?w=1920&q=80" 
                  alt="Developer Documentation" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gray-900/80" />
              </div>
              <div className="container mx-auto px-4 relative z-10">
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="max-w-3xl"
                >
                  <h1 className="text-4xl font-azonix font-bold mb-4">
                    ANIMAC <span className="gradient-text">Developer Documentation</span>
                  </h1>
                  <p className="text-gray-400 text-lg mb-8">
                    Everything you need to build on the ANIMAC platform. From API references to deployment guides.
                  </p>
                  
                  {/* Quick Links */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {quickLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.link}
                        className="flex items-center gap-4 p-4 glass-card rounded-xl border border-[rgba(167,139,250,0.2)] hover:border-[rgba(167,139,250,0.5)] transition-colors group glow-border"
                      >
                        <div className="w-10 h-10 rounded-lg glass-card flex items-center justify-center group-hover:bg-[rgba(167,139,250,0.2)] transition-colors">
                          <link.icon className="text-[#a78bfa]" size={20} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{link.title}</h3>
                          <p className="text-gray-500 text-sm">{link.description}</p>
                        </div>
                        <ChevronRight className="text-gray-600 ml-auto group-hover:text-[#a78bfa] transition-colors" size={20} />
                      </a>
                    ))}
                  </div>
                </motion.div>
              </div>
            </section>

            {/* Content Sections */}
            <section className="container mx-auto px-4 py-12">
              {/* Getting Started */}
              <div id="getting-started" className="mb-16 scroll-mt-20">
                <h2 className="text-2xl font-azonix font-bold mb-6 flex items-center gap-2">
                  <BookOpen className="text-[#a78bfa]" /> Getting Started
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {sections[0].articles.map((article, index) => (
                    <a
                      key={index}
                      href={`#${article.slug}`}
                      className="p-4 glass-card rounded-xl border border-[rgba(167,139,250,0.15)] hover:border-[rgba(167,139,250,0.4)] transition-colors group"
                    >
                      <h3 className="font-semibold text-white mb-1 group-hover:text-[#a78bfa] transition-colors">{article.title}</h3>
                      <p className="text-gray-500 text-sm">Learn the basics of {article.title.toLowerCase()}</p>
                    </a>
                  ))}
                </div>
              </div>

              {/* API Reference */}
              <div id="api" className="mb-16 scroll-mt-20">
                <h2 className="text-2xl font-azonix font-bold mb-6 flex items-center gap-2">
                  <Code className="text-[#a78bfa]" /> API Reference
                </h2>
                
                <div className="glass-card rounded-xl border border-[rgba(167,139,250,0.15)] overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 glass-card border-b border-[rgba(167,139,250,0.15)]">
                    <Terminal size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-400 font-mono">REST API Example</span>
                  </div>
                  <pre className="p-4 overflow-x-auto">
                    <code className="text-sm text-gray-300 font-mono">
                      {codeExamples['rest-api']}
                    </code>
                  </pre>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  {sections[1].articles.slice(1).map((article, index) => (
                    <a
                      key={index}
                      href={`#${article.slug}`}
                      className="p-4 glass-card rounded-xl border border-[rgba(167,139,250,0.15)] hover:border-[rgba(167,139,250,0.4)] transition-colors group"
                    >
                      <h3 className="font-semibold text-white mb-1 group-hover:text-[#a78bfa] transition-colors">{article.title}</h3>
                      <p className="text-gray-500 text-sm">{article.title} documentation</p>
                    </a>
                  ))}
                </div>
              </div>

              {/* Database */}
              <div id="database" className="mb-16 scroll-mt-20">
                <h2 className="text-2xl font-azonix font-bold mb-6 flex items-center gap-2">
                  <Database className="text-[#a78bfa]" /> Database
                </h2>
                
                <div className="glass-card rounded-xl border border-[rgba(167,139,250,0.15)] overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 glass-card border-b border-[rgba(167,139,250,0.15)]">
                    <Terminal size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-400 font-mono">MongoDB Connection</span>
                  </div>
                  <pre className="p-4 overflow-x-auto">
                    <code className="text-sm text-gray-300 font-mono">
                      {codeExamples['mongodb-setup']}
                    </code>
                  </pre>
                </div>
              </div>

              {/* Frontend */}
              <div id="frontend" className="mb-16 scroll-mt-20">
                <h2 className="text-2xl font-azonix font-bold mb-6 flex items-center gap-2">
                  <Layout className="text-[#a78bfa]" /> Frontend Development
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {sections[3].articles.map((article, index) => (
                    <a
                      key={index}
                      href={`#${article.slug}`}
                      className="p-4 glass-card rounded-xl border border-[rgba(167,139,250,0.15)] hover:border-[rgba(167,139,250,0.4)] transition-colors group"
                    >
                      <h3 className="font-semibold text-white mb-1 group-hover:text-[#a78bfa] transition-colors">{article.title}</h3>
                      <p className="text-gray-500 text-sm">{article.title} guide</p>
                    </a>
                  ))}
                </div>
              </div>

              {/* Deployment */}
              <div id="deployment" className="mb-16 scroll-mt-20">
                <h2 className="text-2xl font-azonix font-bold mb-6 flex items-center gap-2">
                  <Globe className="text-[#a78bfa]" /> Deployment
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {sections[4].articles.map((article, index) => (
                    <a
                      key={index}
                      href={`#${article.slug}`}
                      className="p-4 glass-card rounded-xl border border-[rgba(167,139,250,0.15)] hover:border-[rgba(167,139,250,0.4)] transition-colors group"
                    >
                      <h3 className="font-semibold text-white mb-1 group-hover:text-[#a78bfa] transition-colors">{article.title}</h3>
                      <p className="text-gray-500 text-sm">{article.title} guide</p>
                    </a>
                  ))}
                </div>
              </div>

              {/* Security */}
              <div id="security" className="mb-16 scroll-mt-20">
                <h2 className="text-2xl font-azonix font-bold mb-6 flex items-center gap-2">
                  <Shield className="text-[#a78bfa]" /> Security
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {sections[5].articles.map((article, index) => (
                    <a
                      key={index}
                      href={`#${article.slug}`}
                      className="p-4 glass-card rounded-xl border border-[rgba(167,139,250,0.15)] hover:border-[rgba(167,139,250,0.4)] transition-colors group"
                    >
                      <h3 className="font-semibold text-white mb-1 group-hover:text-[#a78bfa] transition-colors">{article.title}</h3>
                      <p className="text-gray-500 text-sm">{article.title} documentation</p>
                    </a>
                  ))}
                </div>
              </div>
            </section>

            {/* Footer CTA */}
            <section className="container mx-auto px-4 py-12">
              <div className="glass-card rounded-2xl p-8 text-center border border-[rgba(167,139,250,0.3)] glow-border">
                <h2 className="text-2xl font-azonix font-bold text-white mb-4">Need Help?</h2>
                <p className="text-gray-400 mb-6 max-w-lg mx-auto">
                  Can't find what you're looking for? Join our Discord community or check out our GitHub repository.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a
                    href="https://discord.gg/animac"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 glass-card border border-[rgba(167,139,250,0.3)] text-[#a78bfa] rounded-lg font-semibold hover:bg-[rgba(167,139,250,0.1)] transition-colors"
                  >
                    Join Discord <ExternalLink size={18} />
                  </a>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3 glass-card border border-[rgba(167,139,250,0.3)] text-white rounded-lg font-semibold hover:bg-[rgba(167,139,250,0.1)] transition-colors"
                  >
                    Contact Support
                  </Link>
                </div>
              </div>
            </section>
          </main>
        </div>
      </motion.div>
    </>
  );
}
